import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Progress } from './ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from '../hooks/use-toast';
import { Sparkles, FileText, Download, Save, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/automatizacion`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('lexduo_token')}` });

export const DialogEscritoIA = ({ open, onClose, email, onSaved }) => {
  const [opciones, setOpciones] = useState({ tipos: [], modelos: {} });
  const [taskType, setTaskType] = useState('escrito_generico');
  const [model, setModel] = useState('claude-opus-4-7');
  const [instructions, setInstructions] = useState('');
  const [usarCorpus, setUsarCorpus] = useState(true);
  const [usarJuris, setUsarJuris] = useState(true);
  const [usarPrevios, setUsarPrevios] = useState(true);
  const [expedienteId, setExpedienteId] = useState('');

  const [fase, setFase] = useState('config'); // config | generando | revision
  const [progreso, setProgreso] = useState(0);
  const [texto, setTexto] = useState('');
  const [meta, setMeta] = useState(null);
  const [tituloSugerido, setTituloSugerido] = useState('');
  const [guardando, setGuardando] = useState(false);
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!open || !email) return;
    setFase('config'); setTexto(''); setProgreso(0); setMeta(null);
    setInstructions('');
    setTaskType(email.ia_tipo_tramite || 'escrito_generico');
    setExpedienteId(email.expediente_id_confirmado || email.ia_expediente_id || '');
    axios.get(`${API}/emails-procuradores/escrito/opciones`, { headers: authHeaders() })
      .then(r => setOpciones(r.data)).catch(() => {});
    return () => { clearInterval(pollRef.current); clearInterval(timerRef.current); };
  }, [open, email]);

  const modelosAnthropic = (opciones.modelos && opciones.modelos.anthropic) || ['claude-opus-4-7', 'claude-sonnet-4-6'];

  const handleGenerar = async () => {
    if (!email) return;
    setFase('generando'); setProgreso(2); setTexto('');
    const estimado = taskType === 'contestacion_demanda' ? 180 : 90;
    let elapsed = 0;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      elapsed += 1;
      setProgreso(Math.min(95, Math.round((elapsed / estimado) * 100)));
    }, 1000);
    try {
      const res = await axios.post(
        `${API}/emails-procuradores/${email.id}/generar-escrito`,
        { task_type: taskType, provider: 'anthropic', model, max_tokens: 12000,
          instructions, usar_corpus: usarCorpus, usar_jurisprudencia: usarJuris,
          usar_escritos_previos: usarPrevios, expediente_id: expedienteId || null },
        { headers: authHeaders() }
      );
      const jobId = res.data.job_id;
      pollRef.current = setInterval(async () => {
        try {
          const p = await axios.get(`${API}/emails-procuradores/escrito/progress/${jobId}`, { headers: authHeaders() });
          const d = p.data;
          if (d.estado === 'completado') {
            clearInterval(pollRef.current); clearInterval(timerRef.current);
            setProgreso(100); setTexto(d.texto || ''); setMeta(d.meta || null);
            setTituloSugerido(d.titulo_sugerido || '');
            setFase('revision');
          } else if (d.estado === 'error') {
            clearInterval(pollRef.current); clearInterval(timerRef.current);
            setFase('config');
            toast({ title: 'Error al generar', description: d.error || 'Error desconocido', variant: 'destructive' });
          }
        } catch (e) {}
      }, 3000);
    } catch (e) {
      clearInterval(timerRef.current); setFase('config');
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo iniciar la generación', variant: 'destructive' });
    }
  };

  const handleDescargarWord = async () => {
    try {
      const res = await axios.post(`${API}/escrito/preview-word`, { texto, filename: tituloSugerido || 'escrito' },
        { headers: authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `${(tituloSugerido || 'escrito').replace(/[^\w-]/g, '_')}.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo generar el Word', variant: 'destructive' });
    }
  };

  const handleGuardar = async () => {
    if (!expedienteId) { toast({ title: 'Falta expediente', description: 'Indica el expediente destino', variant: 'destructive' }); return; }
    setGuardando(true);
    try {
      const res = await axios.post(`${API}/emails-procuradores/${email.id}/escrito-confirmar`,
        { expediente_id: Number(expedienteId), titulo: tituloSugerido || 'Escrito IA', tipo: 'Escrito',
          task_type: taskType, texto, visible_cliente: false },
        { headers: authHeaders() });
      toast({ title: 'Guardado', description: res.data.message || 'Escrito guardado como actuación' });
      onSaved && onSaved(res.data);
      onClose();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo guardar', variant: 'destructive' });
    } finally { setGuardando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="escrito-ia-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" /> Generar escrito completo con IA
          </DialogTitle>
        </DialogHeader>

        {fase === 'config' && (
          <div className="space-y-4" data-testid="escrito-ia-config">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Tipo de escrito</Label>
                <Select value={taskType} onValueChange={setTaskType}>
                  <SelectTrigger data-testid="escrito-ia-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(opciones.tipos || []).map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Modelo IA</Label>
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger data-testid="escrito-ia-modelo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {modelosAnthropic.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-400 mt-1">Opus = máxima calidad (más lento). Sonnet = rápido.</p>
              </div>
            </div>
            <div>
              <Label>Expediente destino (ID)</Label>
              <Input value={expedienteId} onChange={e => setExpedienteId(e.target.value)}
                placeholder="ID del expediente" data-testid="escrito-ia-expediente" />
            </div>
            <div>
              <Label>Instrucciones para el letrado (opcional)</Label>
              <Textarea rows={3} value={instructions} onChange={e => setInstructions(e.target.value)}
                placeholder="Ej.: Enfatizar la mala fe procesal del banco y solicitar imposición de costas."
                data-testid="escrito-ia-instrucciones" />
            </div>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={usarCorpus} onCheckedChange={setUsarCorpus} data-testid="escrito-ia-corpus" />
                <Label className="cursor-pointer">Usar escrito modelo del despacho</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={usarJuris} onCheckedChange={setUsarJuris} data-testid="escrito-ia-juris" />
                <Label className="cursor-pointer">Incluir jurisprudencia verificada</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={usarPrevios} onCheckedChange={setUsarPrevios} data-testid="escrito-ia-previos" />
                <Label className="cursor-pointer">Usar actuaciones previas</Label>
              </div>
            </div>
          </div>
        )}

        {fase === 'generando' && (
          <div className="py-10 space-y-4 text-center" data-testid="escrito-ia-generando">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-amber-500" />
            <p className="font-medium">Redactando el escrito completo…</p>
            <p className="text-sm text-gray-400">Con Opus puede tardar 1-3 minutos. No cierres esta ventana.</p>
            <Progress value={progreso} className="w-full" data-testid="escrito-ia-progress" />
            <p className="text-xs text-gray-400">{progreso}%</p>
          </div>
        )}

        {fase === 'revision' && (
          <div className="space-y-3" data-testid="escrito-ia-revision">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2"><FileText className="h-4 w-4" /> Borrador (editable)</Label>
              {meta && <span className="text-xs text-gray-400">{meta.model} · {meta.tokens_output} tokens · {Math.round(meta.duracion_segundos)}s</span>}
            </div>
            <p className="text-xs text-amber-600">Revisa el texto. Las marcas [VERIFICAR] señalan datos a completar antes de presentar.</p>
            <Textarea rows={20} value={texto} onChange={e => setTexto(e.target.value)}
              className="font-mono text-sm" data-testid="escrito-ia-texto" />
            <div>
              <Label>Título de la actuación</Label>
              <Input value={tituloSugerido} onChange={e => setTituloSugerido(e.target.value)} data-testid="escrito-ia-titulo" />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {fase === 'config' && (
            <>
              <Button variant="outline" onClick={onClose} data-testid="escrito-ia-cancelar">Cancelar</Button>
              <Button onClick={handleGenerar} data-testid="escrito-ia-generar" className="bg-amber-500 hover:bg-amber-600">
                <Sparkles className="h-4 w-4 mr-2" /> Generar escrito
              </Button>
            </>
          )}
          {fase === 'revision' && (
            <>
              <Button variant="outline" onClick={() => setFase('config')} data-testid="escrito-ia-volver">Volver a generar</Button>
              <Button variant="outline" onClick={handleDescargarWord} data-testid="escrito-ia-descargar">
                <Download className="h-4 w-4 mr-2" /> Descargar Word
              </Button>
              <Button onClick={handleGuardar} disabled={guardando} data-testid="escrito-ia-guardar" className="bg-green-600 hover:bg-green-700">
                {guardando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Guardar como actuación
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DialogEscritoIA;
